import { HttpErrorResponse } from '@angular/common/http';
import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
  ChangeDetectionStrategy,
  inject
} from '@angular/core';
import { ActionIconDef, Grid as InavistaGrid, GridActionIconClickEvent, GridConfig, GridRow, GridRowCommitEvent } from '@inavista/components/grid';
import { UploaderComponent } from '@syncfusion/ej2-angular-inputs';
import { ApiResponse, Companies, UserRoles } from 'projects/service/src/app/shared/models/app.model';
import { Attachment } from 'projects/service/src/app/shared/models/attachment.model';
import { AttachmentsService } from 'projects/service/src/app/shared/services/attachments.service';
import { CommonService } from 'projects/service/src/app/shared/services/common.service';
import { downloadResource } from 'projects/service/src/app/shared/utils/general-utils';
import { exportInavistaGrid } from 'projects/service/src/app/shared/utils/inavista-grid-export';
import { LocalRowsGridApi, provideLocalGridRows } from 'projects/service/src/app/shared/utils/local-rows-grid-api';
import { environment } from 'projects/service/src/environments/environment';
import { ServiceOrderService } from '../../services/service-order.service';
import { returnOrderTypes } from 'projects/service/src/app/modules/home/models/orders.model';
import { ATTACHMENT_COLUMNS, ATTACHMENT_ROW_KEY, AttachmentsGridContext, buildAttachmentsActionIcons, buildAttachmentsGridConfig } from './attachments.grid.config';


const BASE_URL = environment.apiUrl;

let USER;
@Component({
    selector: 'app-attachments-tab',
    templateUrl: './attachments-tab.component.html',
    styleUrls: ['./attachments-tab.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
    // The Attachments grid reads this.attachments, not an endpoint.
    providers: [...provideLocalGridRows()]
})
export class AttachmentsTabComponent implements OnInit, OnChanges {
  loading: boolean = false;
  isGridView: boolean = true;
  addingAttachment: boolean = true;
  allowedExtensions = this.commonService.allowedExtenstions;

  @ViewChild('attachmentUpload')
  public uploadObj: UploaderComponent;

  public path: Object = {
    saveUrl: `${BASE_URL}BlobManager/UploadTempBlobAsync`,
    removeUrl: 'https://ej2.syncfusion.com/services/api/uploadbox/Remove'
  };
  public dropElement: HTMLElement = document.getElementsByClassName('uploader-section')[0] as HTMLElement;

  attachmentsConfig: GridConfig = null;
  attachmentsActionIcons: ActionIconDef[] = [];
  /** A row of the Attachments grid is open for editing (the old grid.isEdit). */
  attachmentEditing = false;
  lastClickedAttachment: GridRow = null;
  private readonly attachmentsRows = inject(LocalRowsGridApi);
  @Input() attachments: Attachment[] = [];
  @Input() customerData: any;
  @Input() serviceOrderId = null;
  @Input() serviceOrderIdLN = null;
  @Input() moduleId = null;
  @Input() module = null;
  @Input() isEditable: boolean = true;
  @Input() canEdit: boolean = true;
  @Input() fieldRules: any;

  @ViewChild('attachmentsGrid') grid: InavistaGrid;
  @Output() attachmentsUpdated = new EventEmitter();
  showEmailPopup: boolean = false;
  emailAttachment: Attachment;
  emailSubject: string = `Service Order`;
  link: string = null;
  attachmentReadOnly: boolean = false
  returnOrderTypes: { returnOrder: string; coreReturnOrder: string; damagedReturnOrder: string; };
  employeeName: any;

  constructor(
    private serviceOrderService: ServiceOrderService,
    private commonService: CommonService,
    private attachmentService: AttachmentsService
  ) {
    USER = this.commonService.USER;
    this.employeeName = this.commonService.USER.employeeName;
    this.returnOrderTypes = returnOrderTypes;
  }

  // A click elsewhere saves the open row, as the old grid's endEdit did (saveBatch saves the open row in 'row' mode).
  @HostListener('document:click', ['$event'])
  onClick(event) {
    const target = event.target as HTMLElement;
    // Not a click in the grid, its menus, or a confirmation.
    if (this.attachmentEditing && !target?.closest?.('.attachments-grid, [class*="inavista"], .swal2-container')) {
      this.grid?.saveBatch();
    }
  }

  ngOnInit(): void {
    this.attachments = this.attachments === undefined ? [] : this.attachments;
    this.attachmentReadOnly = this.commonService.roleClaims['SO_Popup_Field_attachments']?.readOnly
    if (this.commonService.roleClaims['Attachment_Tab_isEditable']?.edit) {
      this.isEditable = true;
    }
    if (this.isEditable && this.attachmentReadOnly) {
      this.isEditable = false;
    }
    if (!this.isEditable) { this.isGridView = true; this.addingAttachment = false; }
    this.initGrid();
    if (this.attachments && this.attachments.length) {
      this.isGridView = true;
      this.addingAttachment = false;
    }

    if ([returnOrderTypes.coreReturnOrder, returnOrderTypes.damagedReturnOrder].includes(this.module)) {
      if (this.module === returnOrderTypes.coreReturnOrder) {
        this.emailSubject = `Attachment  - Core Return order ${this.moduleId}`;
      } else if (this.module === returnOrderTypes.damagedReturnOrder) {
        this.emailSubject = `Attachment  - Damaged Return order ${this.moduleId}`;
      }
    } else {
      this.emailSubject = `Attachment of Service Order: ${this.commonService.USER.company === Companies.kingslynn ? this.serviceOrderIdLN || this.serviceOrderId : this.serviceOrderId}`;
    }

    this.attachments?.map((attachment, index) => {
      attachment.tempId = attachment.attachmentId;
      attachment.serialNumber = index + 1;
      let lastUpdated = new Date(attachment.lastUpdated).setHours(0, 0, 0);
      attachment.lastUpdatedDate = new Date(lastUpdated);
      attachment.fileType = attachment.fileName.split('.')[attachment.fileName.split('.').length - 1];
    });
    this.refreshAttachmentsGrid();
  }

  ngOnChanges(change: SimpleChanges) {
    if (change && change.attachments && change.attachments.currentValue && change.attachments.previousValue) {
      this.attachments = change.attachments.currentValue;
      this.attachments.map((attachment, index) => {
        let lastUpdated = new Date(attachment.lastUpdated).setHours(0, 0, 0);
        attachment.lastUpdatedDate = new Date(lastUpdated);
        attachment.serialNumber = index + 1;
        attachment.fileType = attachment.fileName.split('.')[attachment.fileName.split('.').length - 1];
      });
      this.isGridView = true;
      this.addingAttachment = false;
      this.refreshAttachmentsGrid();
    }
  }

  // Attachments on @inavista/components: the rows are this.attachments, served by LocalRowsGridApi (no endpoint).
  initGrid() {
    this.attachmentsRows.columns.set(LocalRowsGridApi.schema(ATTACHMENT_COLUMNS));
    const ctx: AttachmentsGridContext = {
      // As before (beginEdit): Comments can't be edited with the read-only attachments field claim.
      editLockReason: () => this.attachmentReadOnly ? 'You do not have permission to change attachments.' : null,
      // As before (rowDataBound): Email is enabled only on a read-only attachment, Delete only on one that is not.
      emailLockReason: row => row['readOnly'] ? null : 'Email is available on read-only attachments only.',
      deleteLockReason: row => row['readOnly'] ? 'This attachment is read-only and can\'t be deleted.' : null,
      fileSize: size => this.getFileSize(size),
      canExport: !!this.commonService.roleClaims['AllGrid_Toolbar_Excel_Export']?.visible,
    };
    this.attachmentsConfig = buildAttachmentsGridConfig(ctx);
    this.attachmentsActionIcons = buildAttachmentsActionIcons(ctx);
  }

  // The rows the grid shows: this.attachments, keyed by index. Last Updated as a local day, as the old lastUpdatedDate.
  refreshAttachmentsGrid() {
    const day = (value: any) => {
      const date = value ? new Date(value) : null;
      if (!date || isNaN(date.getTime())) return null;
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    };
    this.attachmentsRows.rows.set((this.attachments ?? []).map((a: any, index) => ({
      ...a,
      [ATTACHMENT_ROW_KEY]: index,
      lastUpdatedDate: day(a.lastUpdatedDate),
    })));
    this.grid?.refresh();
  }

  /** The grid's height: the old grid had none and grew with its rows (toolbar + header + rows). */
  get attachmentsGridHeight(): number {
    return 80 + Math.max(this.attachments?.length ?? 0, 1) * 30;
  }

  private attachmentOf(row: GridRow): any {
    return this.attachments?.[row?.[ATTACHMENT_ROW_KEY] as number];
  }

  beforeFileUpload(args: any) {
    const token: string = localStorage.getItem('access_token');
    if (args.fileData.name.length > 250) {
      args.cancel = true;
    } else {
      args.currentRequest.setRequestHeader('Authorization', `Bearer ${token}`);
      this.serviceOrderService.popupLoader.next(true);
    }
  }

  onUploaderCreated(uploader: UploaderComponent) {
    if (uploader) {
      uploader.dropArea = document.getElementsByClassName('uploader-section')[0] as HTMLElement;
    }
  }

  onUploadSuccess(args: any) {
    const file = args.file;
    const response = JSON.parse(args.e.srcElement.responseText);
    const attachment = {
      attachmentId: 0,
      tempId: this.attachments.length + 1,
      serialNumber: this.attachments.length + 1,
      fileName: file.name,
      lastUpdated: new Date(),
      lastUpdatedDate: new Date(),
      size: file.size,
      fileSize: file.size,
      comment: 'New file',
      fileType: file.type.toLowerCase(),
      fileId: response.result.fileId,
      uri: response.result.uri,
      uploadedByName: USER.userName,
      uploadedById: USER.userId
    }
    this.loading = true;
    this.attachmentService.saveAttachments({
      id: this.moduleId,
      moduleName: this.module,
      attachments: [attachment]
    }).subscribe((res: ApiResponse) => {
      if (res.isSuccessful && res.result) {
        res.result?.map((attachment, index) => {
          attachment.tempId = attachment.attachmentId;
          attachment.serialNumber = index + 1;
          let lastUpdated = new Date(attachment.lastUpdated).setHours(0, 0, 0);
          attachment.lastUpdatedDate = new Date(lastUpdated);
          attachment.fileType = attachment.fileName.split('.')[attachment.fileName.split('.').length - 1];
        });
        this.attachments = res.result;
        this.refreshAttachmentsGrid();
        this.attachmentsUpdated.emit(this.attachments);
      } else {
        this.commonService.showNotification('error', res.message);
      }
      this.loading = false;
    }, error => {
      this.loading = false;
      this.commonService.showNotification('error', error.message);
    });
  }

  onActionComplete(args: any) {
    if (args.name === 'actionComplete' && args.fileData) {
      this.serviceOrderService.popupLoader.next(false);
      this.addingAttachment = false;
      // this.attachmentsUpdated.emit(this.attachments);
    }
  }

  // A double click on a row while Comments are locked says why, as the old beginEdit cancel did. The grid has no
  // double-click output, so the row comes from its click and the double click from the wrapper.
  onAttachmentDoubleClick() {
    if (this.lastClickedAttachment && this.attachmentReadOnly) {
      this.commonService.showPermissionNotification();
    }
  }

  // Enter, the check icon or a click elsewhere: the Comments edit is saved at once, as before (Attachment/UpdateAttachment).
  onAttachmentCommit(commit: GridRowCommitEvent) {
    this.attachmentEditing = false;
    const attachment = this.attachmentOf(commit.row);
    if (!attachment) return;
    // The edited row, as the old grid's args.data: the attachment with the new comment.
    const edited = { ...attachment, comment: commit.row['comment'] };
    const data = this.setDataForUpdate(edited);
    if (edited.attachmentId) {
      this.loading = true;
      this.attachmentService.update(data).subscribe(res => {
        this.loading = false;
        this.grid?.saveSucceeded(commit, commit.row);
        this.updateAttachmentLocally(edited);
        this.commonService.showNotification('success', 'Attachment updated successfully')
      },
        error => {
          this.loading = false;
          this.commonService.showNotification('error', 'Attachment update failed!')
          this.grid?.saveFailed(commit, { kind: 'conflict', message: 'Attachment update failed!' });
        });
    } else {
      this.grid?.saveSucceeded(commit, commit.row);
      this.updateAttachmentLocally(edited);
    }
  }

  updateAttachmentLocally(data) {
    const index = this.attachments.findIndex(att => att.tempId === data.tempId);
    this.attachments[index].comment = data.comment;
    this.attachments[index].lastUpdated = new Date();
    this.refreshAttachmentsGrid();
  }

  setDataForUpdate(data) {
    return {
      id: this.moduleId,
      moduleName: this.module,
      lastUpdatedBy: data.uploadedById,
      lastUpdatedByName: data.uploadedByName,
      attachment: {
        attachmentId: data.attachmentId,
        fileName: data.fileName,
        fileId: data.fileId,
        uploadedById: data.uploadedById,
        uploadedByName: data.uploadedByName,
        lastUpdated: data.lastUpdated,
        uri: data.uri,
        fileSize: data.fileSize,
        size: data.size,
        comment: data.comment,
        readOnly: false
      }
    }
  }

  // Excel export (only with the export claim): "Attachments(<date>).xlsx", as before.
  onExportRequested() {
    exportInavistaGrid(this.grid, 'Attachments');
  }

  // Email / Download / Delete: the old command buttons, same calls. Disabled icons never reach here.
  onAttachmentActionIconClick(event: GridActionIconClickEvent) {
    const data = this.attachmentOf(event.row);
    if (!data) return;
    if (event.iconKey === 'download') {
      downloadResource(`${BASE_URL}BlobManager/DownloadFile?fileUrl=${data.uri}`, data.fileName);
    } else if (event.iconKey === 'email') {
      this.emailAttachment = data;
      this.showEmailPopup = true;
      this.link = data.uri;
    } else if (event.iconKey === 'delete') {
      if (!data.readOnly) {
        this.deleteAttachment(data);
      }
    }
  }

  deleteAttachment(data: any) {
    if (this.attachmentReadOnly) {
      this.commonService.showPermissionNotification();
    } else {
      this.commonService.showConfirmation('Are you sure you want to delete this attachment?')
        .then(result => {
          if (result.isConfirmed) {
            if (this.moduleId && data.attachmentId) {
              const attachment = {
                id: this.moduleId,//ItemOrderId
                moduleName: this.module,
                attachmentId: data.attachmentId,
                lastUpdatedBy: USER.userId,
                lastUpdatedByName: USER.userName
              }
              this.loading = true;
              this.attachmentService.delete(attachment)
                .subscribe((res: ApiResponse) => {
                  this.loading = false;
                  if (res.isSuccessful) {
                    const index = this.attachments.findIndex(att => att.attachmentId === data.attachmentId);
                    this.commonService.showNotification('success', 'Attachment deleted successfully!');
                    this.removeAttachment(index);
                    this.attachmentsUpdated.emit(this.attachments);
                  }
                }, (error: HttpErrorResponse) => {
                  this.commonService.showNotification('error', 'Attachment delete failed!');
                  this.loading = false;
                });
            } else {
              const index = this.attachments.findIndex(att => att.tempId === data.tempId);
              this.removeAttachment(index);
              this.attachmentsUpdated.emit(this.attachments);
            }
          }
        });
    }
  }

  removeAttachment(index: number) {
    if (index > -1) {
      this.attachments.splice(index, 1);
    }
    this.refreshAttachmentsGrid();
  }

  getFileSize(size: any) {
    size = +size;
    return size > 1e+6 ? Math.round(size / 1e+6) + " MB" : Math.round(size / 1000) + " KB"
  }

  downloadFile(uri: string, fileName: string) {
    downloadResource(uri, fileName);
  }

  onClose() {
    this.showEmailPopup = false;
  }

}
